import React from 'react';
import { Clock, Gift, AlertTriangle, TrendingUp, Package, RefreshCw } from 'lucide-react';

/**
 * Reusable Subscription Components
 * Dynamic, configurable components for subscription page
 */

// Usage metric configuration
export const USAGE_METRICS = [
  {
    key: 'firebase_operations',
    label: 'Firebase Operations',
    icon: 'Database',
    color: 'blue',
    format: (value) => value.toLocaleString()
  },
  {
    key: 'storage_mb',
    label: 'Storage',
    icon: 'HardDrive',
    color: 'purple',
    format: (value) => `${value.toFixed(1)} MB`
  }
];

// Progress bar with smart coloring
export const ProgressBar = ({ current, total, className = '' }) => {
  const percentage = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  
  const getColor = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    if (percentage >= 60) return 'bg-blue-500';
    return 'bg-green-500';
  };

  return (
    <div className={`bg-gray-200 rounded-full h-3 overflow-hidden ${className}`}>
      <div
        className={`h-3 rounded-full transition-all duration-500 ease-out ${getColor()}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Usage metric row component
export const UsageMetric = ({ icon: Icon, label, current, total, formatValue }) => {
  const percentage = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" />}
          <span className="font-medium text-gray-700">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900">
            {formatValue ? formatValue(current) : current.toLocaleString()} / {formatValue ? formatValue(total) : total.toLocaleString()}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            percentage >= 90 ? 'bg-red-100 text-red-700' :
            percentage >= 80 ? 'bg-yellow-100 text-yellow-700' :
            'bg-green-100 text-green-700'
          }`}>
            {percentage}%
          </span>
        </div>
      </div>
      <ProgressBar current={current} total={total} />
    </div>
  );
};

// Plan header card
export const PlanHeader = ({ plan, daysRemaining, endDate, formatDate }) => {
  const getDaysColor = () => {
    if (daysRemaining <= 3) return 'text-red-400';
    if (daysRemaining <= 7) return 'text-yellow-400';
    return 'text-white';
  };

  return (
    <div className="relative bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 rounded-2xl shadow-2xl p-8 text-white overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
      
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <div className="text-sm font-medium opacity-90 mb-2 uppercase tracking-wide">Your Plan</div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-2">{plan.planName}</h2>
            {plan.isTrial && (
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400 bg-opacity-30 rounded-full">
                <Gift className="w-4 h-4" />
                <span className="text-sm font-semibold">Free Trial</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="text-4xl sm:text-5xl font-bold">₹{plan.price}</div>
            <div className="text-sm opacity-90 font-medium">
              {plan.billing === 'yearly' ? '/year' : '/month'}
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs opacity-80 mb-1">Validity</div>
              <div className={`text-lg font-bold ${getDaysColor()}`}>
                {daysRemaining} days remaining
              </div>
            </div>
          </div>
          <div className="text-sm">
            <div className="opacity-80 mb-1">Valid until</div>
            <div className="font-bold text-lg">{formatDate(endDate)}</div>
          </div>
        </div>

        {plan.bonusDays && plan.bonusDays.total > 0 && (
          <div className="mt-4 flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-orange-400 bg-opacity-30 rounded-xl p-4">
            <Gift className="w-6 h-6" />
            <div>
              <div className="font-bold text-lg">🎉 {plan.bonusDays.total} Bonus Days!</div>
              <div className="text-sm opacity-90">
                {plan.bonusDays.source && `From: ${plan.bonusDays.source}`}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Topup card component
export const TopupCard = ({ topup }) => {
  return (
    <div className="group p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 hover:border-green-300 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h4 className="font-bold text-gray-900 text-lg mb-1">{topup.name}</h4>
          {topup.source === 'plan_upgrade' && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
              <Gift className="w-3 h-3" />
              UPGRADE BONUS
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
          Active
        </span>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {topup.remaining?.firebase_operations > 0 && (
          <div className="bg-white rounded-lg p-3 border border-green-100">
            <div className="text-xs text-gray-600 mb-1">Operations</div>
            <div className="text-lg font-bold text-green-700">
              {topup.remaining.firebase_operations.toLocaleString()}
            </div>
          </div>
        )}
        {topup.remaining?.storage_mb > 0 && (
          <div className="bg-white rounded-lg p-3 border border-purple-100">
            <div className="text-xs text-gray-600 mb-1">Storage</div>
            <div className="text-lg font-bold text-purple-700">
              {topup.remaining.storage_mb} MB
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Usage section component
export const UsageSection = ({ usage, entitlements, onRefresh }) => {
  const metrics = Object.keys(entitlements).map(key => ({
    key,
    current: usage[key] || 0,
    total: entitlements[key],
    config: USAGE_METRICS.find(m => m.key === key)
  })).filter(m => m.config);

  const getIcon = (iconName) => {
    const icons = {
      'Database': require('lucide-react').Database,
      'HardDrive': require('lucide-react').HardDrive
    };
    return icons[iconName];
  };

  // Check if any metric is in warning zone
  const hasWarnings = metrics.some(m => {
    const percentage = m.total > 0 ? (m.current / m.total) * 100 : 0;
    return percentage >= 80;
  });

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
            <RefreshCw className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Current Usage</h3>
            <p className="text-sm text-gray-500">This billing period</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors group"
          title="Refresh usage"
        >
          <RefreshCw className="w-5 h-5 text-gray-600 group-hover:rotate-180 transition-transform duration-500" />
        </button>
      </div>

      {/* Warning Banner */}
      {hasWarnings && (
        <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-yellow-900 mb-1">Usage Alert</div>
              <div className="text-sm text-yellow-800">
                You're approaching your limits. Consider upgrading or buying a top-up.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Metrics */}
      <div className="space-y-6">
        {metrics.map(metric => {
          const Icon = getIcon(metric.config.icon);
          return (
            <UsageMetric
              key={metric.key}
              icon={Icon}
              label={metric.config.label}
              current={metric.current}
              total={metric.total}
              formatValue={metric.config.format}
            />
          );
        })}
      </div>
    </div>
  );
};

// Action buttons component
export const SubscriptionActions = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <button
        onClick={() => window.location.href = '/upgrade-plan'}
        className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        <div className="relative z-10 flex items-center justify-center gap-3">
          <TrendingUp className="w-6 h-6 group-hover:translate-y-[-2px] transition-transform" />
          <span className="text-lg">Upgrade Plan</span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity"></div>
      </button>
      
      <button
        onClick={() => window.location.href = '/buy-topup'}
        className="group relative overflow-hidden bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        <div className="relative z-10 flex items-center justify-center gap-3">
          <Package className="w-6 h-6 group-hover:rotate-12 transition-transform" />
          <span className="text-lg">Buy Top-up</span>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent opacity-0 group-hover:opacity-20 transition-opacity"></div>
      </button>
    </div>
  );
};































